import {
  deletePlaceSearchHistory,
  fetchLocationsAroundPosition,
  getPlaceSearchHistory,
  getPlaceSearchSuggestions,
} from "@/api/places";
import PlaceRatingAndType from "@/components/place-mongo-search-dynamic-dropdown/place-rating-and-type";
import Iconify from "@/components/ui/Iconify";
import { useAuthContext } from "@/context/auth/useAuthContext";
import {
  calculateDistanceBetweenCoords,
  formatDistance,
} from "@/utils/helpers";
import { useTranslations } from "@/lib/static-intl";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";

const PlacesSearchSuggestion = ({
  isOpen,
  onClose,
  searchInp,
  triggerPlaceSearch,
  triggerPlaceDetailsModal,
  currentUserLocation,
  setSearchInp,
  handleSearchAroundMyLocation,
}) => {
  const { user } = useAuthContext();
  const t = useTranslations("places");
  const apiT = useTranslations("api");

  const [debounceValue, setDebounceValue] = useState(searchInp || "");

  const [loader, setLoader] = useState(true);

  const [autoCompleteSuggestions, setAutoCompleteSuggestions] = useState([]);
  const [placesSuggestions, setPlacesSuggestions] = useState([]);
  const [placesNearMe, setPlacesNearMe] = useState([]);
  const [searchHistory, setSearchHistory] = useState([]);

  const handleSearch = (_search) => {
    if (_search?.length > 0) {
      setSearchInp(_search);
      triggerPlaceSearch(_search);
    }
  };

  const fetchSearchSuggestions = async (_search) => {
    try {
      setLoader(true);
      const payload = {
        search: _search,
      };
      const response = await getPlaceSearchSuggestions(payload);
      if (response?.placeSuggestions) {
        setPlacesSuggestions(
          structurePlacesSearchSuggestion(
            response?.placeSuggestions,
            currentUserLocation
          )
        );
      }
      if (response?.postSearchSuggestions) {
        setAutoCompleteSuggestions(
          structurePlacesSearchAutoCompletions(
            response?.postSearchSuggestions,
            response?.placeSuggestions,
            searchInp
          )
        );
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    } finally {
      setLoader(false);
    }
  };

  const fetchPlacesNearMe = async (myLocation) => {
    if (!myLocation) return;
    try {
      const response = await fetchLocationsAroundPosition({
        position: {
          lat: myLocation?.latitude,
          lng: myLocation?.longitude,
        },
        maxResults: 20,
      });
      if (Array.isArray(response)) {
        setPlacesNearMe(
          structurePlacesSearchSuggestion(response, currentUserLocation)?.map(
            (s) => ({
              ...s,
              id: s?._id,
            })
          )
        );
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    }
  };

  const fetchPlaceSearchHistory = async () => {
    if (!user?.userId) return;
    try {
      const response = await getPlaceSearchHistory(user?.userId);
      if (Array.isArray(response)) setSearchHistory(response);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    }
  };

  const handleDeletePlaceSearchHistory = async (searchIds) => {
    try {
      setSearchHistory((prev) =>
        prev?.filter((p) => !searchIds.includes(p?.id))
      );
      await deletePlaceSearchHistory([...searchIds]);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    }
  };

  const renderHighlightedText = (renderText, matchText, className) => {
    const matchChars = new Set(matchText.toLowerCase());

    const highlightedText = renderText.split("").map((char, index) => {
      const isMatch = matchChars.has(char.toLowerCase());

      return (
        <span
          key={index}
          className={isMatch ? "font-bold text-red" : "font-normal"}
        >
          {char}
        </span>
      );
    });

    return <span className={className}>{highlightedText}</span>;
  };

  useEffect(() => {
    const handler = setTimeout(() => setDebounceValue(searchInp), 180);
    return () => clearTimeout(handler);
  }, [searchInp]);

  useEffect(() => {
    if (debounceValue?.length > 0) {
      setLoader(true);
      fetchSearchSuggestions(debounceValue);
    }
  }, [debounceValue]);

  useEffect(() => {
    if (currentUserLocation) fetchPlacesNearMe(currentUserLocation);
  }, [currentUserLocation]);

  useEffect(() => {
    if (user?.userId) fetchPlaceSearchHistory();
  }, [user]);
  return (
    <>
      {isOpen && (
        <div className="absolute top-[calc(100%+8px)] left-0 right-0 z-30 w-full rounded-2xl border border-black/5 dark:border-white/10 shadow-[0_12px_32px_rgba(0,0,0,0.12)] py-2 px-1.5 flex flex-col bg-white dark:bg-dark_bg_grey max-h-[60vh] overflow-y-auto overflow-x-hidden overscroll-contain thin-scrollbar">
          {searchInp?.length === 0 ? (
            <>
              <button
                type="button"
                className="w-full flex items-center gap-2 py-2 px-3 hover:bg-black/5 rounded text-sm transition ease-in-out duration-200 font-medium"
                onClick={() => {
                  setSearchInp("");
                  handleSearchAroundMyLocation();
                  onClose();
                }}
              >
                <Iconify
                  icon={"material-symbols:my-location-outline"}
                  className={"!text-red"}
                />
                {t("search around me")}
              </button>
              {searchHistory?.length > 0 && (
                <div className="w-full flex flex-col gap-1 my-2 border-b pb-1 dark:border-brown">
                  <div className="w-full flex items-center justify-between px-2">
                    <span className="text-black/40 dark:text-white/40 text-sm tracking-tight font-medium">
                      {t("recent search")}
                    </span>
                    <button
                      type="button"
                      className="text-black/40 dark:text-white/40 text-sm tracking-tight active:scale-95 transition"
                      onClick={() => {
                        handleDeletePlaceSearchHistory(
                          searchHistory?.map((h) => h?.id)
                        );
                      }}
                    >
                      {t("clear search")}
                    </button>
                  </div>
                  {searchHistory?.slice(0, 5)?.map((history, index) => (
                    <div
                      key={index}
                      className="w-full px-2.5 py-2.5 flex items-center justify-between active:bg-black/5 rounded transition ease-in-out duration-200 cursor-pointer"
                      onClick={() => {
                        handleSearch(history?.search);
                      }}
                    >
                      <div className="w-fit flex items-center gap-1.5 text-sm">
                        <Iconify
                          icon={"iconamoon:search-thin"}
                          className={"!w-4 !h-4"}
                        />
                        {history?.search}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeletePlaceSearchHistory([history?.id]);
                        }}
                        className="hover:bg-black/10 rounded-full p-0.5 transition"
                      >
                        <Iconify
                          icon={"material-symbols-light:close"}
                          className={"!w-4 !h-4"}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {placesNearMe?.slice(0, 5)?.map((item, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => triggerPlaceDetailsModal(item)}
                  className="w-full flex flex-row gap-1 py-2 px-2 hover:bg-black/5 rounded transition ease-in-out duration-200"
                >
                  <span className="flex flex-col text-black/60 dark:text-white/60 items-center gap-1 md:gap-px">
                    <Iconify
                      icon={"mdi:location"}
                      className={"md:!w-8 md:!h-8 !text-inherit"}
                    />
                    {!isNaN(Number(item?.distance)) && (
                      <span className="text-[11px] text-black/90 dark:text-white/90 font-semibold tracking-tight leading-none">
                        {formatDistance(Number(item?.distance))}
                      </span>
                    )}
                  </span>
                  <div className="w-full flex flex-col gap-2 overflow-hidden">
                    <div className="w-full flex items-center justify-between gap-0">
                      {renderHighlightedText(
                        `${
                          item?.name ||
                          item?.city ||
                          item?.state ||
                          item?.country ||
                          ""
                        }`,
                        searchInp,
                        "w-full max-w-[90%] text-sm text-start truncate"
                      )}
                      <PlaceRatingAndType
                        key={item?._id || item?.id + index}
                        place={item}
                        placeType={item?.type}
                      />
                    </div>
                    {renderHighlightedText(
                      `${item?.address || item?.old_address || ""}`,
                      searchInp,
                      "w-full max-w-[90%] text-xs text-wrap break-words leading-tight text-start"
                    )}
                  </div>
                </button>
              ))}
            </>
          ) : (
            <>
              {autoCompleteSuggestions?.length > 0 && (
                <div className="w-full flex flex-col border-b dark:border-b-light_secondary_label pb-2">
                  {autoCompleteSuggestions?.slice(0, 5)?.map((item, index) => (
                    <div
                      key={index}
                      className="w-full px-2.5 py-2.5 flex items-center justify-start hover:bg-black/5 rounded transition ease-in-out duration-200 cursor-pointer"
                      onClick={() => {
                        handleSearch(item);
                      }}
                    >
                      <div className="w-fit flex items-center gap-1.5 text-sm">
                        <Iconify
                          icon={"iconamoon:search-thin"}
                          className={"!w-4 !h-4"}
                        />
                        <span>
                          {renderHighlightedText(
                            `${item}`,
                            searchInp,
                            "text-sm w-full max-w-[90%] truncate"
                          )}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {placesSuggestions?.slice(0, 5)?.map((item, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => triggerPlaceDetailsModal(item)}
                  className="w-full flex flex-row gap-1 py-2 px-2 hover:bg-black/5 rounded transition ease-in-out duration-200"
                >
                  <span className="flex flex-col text-black/60 dark:text-white/60 items-center gap-1 md:gap-px">
                    <Iconify
                      icon={"mdi:location"}
                      className={"md:!w-8 md:!h-8 !text-inherit"}
                    />
                    {!isNaN(Number(item?.distance)) && (
                      <span className="text-[11px] text-black/90 dark:text-white/90 font-semibold tracking-tight leading-none">
                        {formatDistance(Number(item?.distance))}
                      </span>
                    )}
                  </span>
                  <div className="w-full flex flex-col gap-2 overflow-hidden">
                    <div className="w-full flex items-center justify-between gap-0">
                      {renderHighlightedText(
                        `${
                          item?.name ||
                          item?.city ||
                          item?.state ||
                          item?.country ||
                          ""
                        }`,
                        searchInp,
                        "w-full max-w-[90%] text-sm text-start truncate"
                      )}
                      <PlaceRatingAndType
                        key={item?._id || item?.id + index}
                        place={item}
                        placeType={item?.type}
                      />
                    </div>
                    {renderHighlightedText(
                      `${item?.address || item?.old_address || ""}`,
                      searchInp,
                      "w-full max-w-[90%] text-xs text-wrap break-words leading-tight text-start"
                    )}
                  </div>
                </button>
              ))}
              {!loader &&
                autoCompleteSuggestions?.length === 0 &&
                placesSuggestions?.length === 0 && (
                  <div className="w-full flex items-center justify-center gap-1 py-8 px-3 font-medium text-xs text-black/60 dark:text-white/60 tracking-tight text-nowrap">
                    {t("no search result")} <b>'{searchInp}'</b>
                  </div>
                )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default PlacesSearchSuggestion;

const structurePlacesSearchSuggestion = (placeSuggestions, currentLoc) => {
  return placeSuggestions
    ?.map((_s) => {
      const coords1 = {
        latitude: _s?.latitude,
        longitude: _s?.longitude,
      };

      const coords2 = {
        latitude: currentLoc?.latitude,
        longitude: currentLoc?.longitude,
      };

      return {
        ..._s,
        distance: calculateDistanceBetweenCoords(coords1, coords2),
      };
    })
    ?.sort((a, b) => a?.distance - b?.distance);
};

const structurePlacesSearchAutoCompletions = (
  postSearchSuggestions,
  placeSuggestions,
  _searchInputVal
) => {
  const array = [
    ...(postSearchSuggestions || []),
    ...(placeSuggestions
      ?.map((_p) => _p?.name)
      ?.filter((_p) => _p?.includes(_searchInputVal)) || []),
  ];
  return array?.filter((item, index) => array.indexOf(item) === index);
};
